import { useMemo, useState } from "react"
import { AppLayout } from "@/components/layout/app-layout"
import { useSearchParamState } from "@/lib/utils/use-search-param-state"
import { useDebounce } from "@/lib/utils/use-debounce"
import { useOrganization } from "@/lib/organization/organization-context"
import { useTeamMembers } from "@/lib/api/use-teams"
import { Roles } from "@/lib/team/roles"
import {
  useSalesLeadsPending,
  useSalesLeadsAssigned,
  useAssignSalesRep,
} from "@/lib/api/use-sales-leads"
import {
  salesLeadCustomerName,
  salesLeadVehicleName,
  salesLeadVin,
  salesLeadEstimateValue,
  salesLeadUserName,
  type ApiSalesLead,
  type SalesLeadDepartment,
  type TeamMemberSummary,
} from "@/lib/api/types"
import {
  SearchLg as Search,
  Phone,
  Mail01 as Mail,
  UserPlus01 as UserPlus,
  CheckCircle,
  ClockCheck as Clock,
  TrendUp01 as TrendingUp,
  User01 as User,
  XClose,
} from "@untitledui/icons"
import { AnimatePresence, motion } from "motion/react"
import { toast } from "sonner"

const DEPT_LABEL: Record<SalesLeadDepartment, string> = { new: "New", used: "Used" }
const DEPT_BADGE: Record<SalesLeadDepartment, string> = {
  new: "bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200",
  used: "bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200",
}

export function SalesManagerView() {
  const { activeDealershipId } = useOrganization()
  const dealershipTeamId = activeDealershipId ? Number(activeDealershipId) : null

  const [searchQuery, setSearchQuery] = useState("")
  const debouncedSearch = useDebounce(searchQuery, 300)
  const [selectedTab, setSelectedTab] = useSearchParamState("tab", "pending")
  const [assignTarget, setAssignTarget] = useState<ApiSalesLead | null>(null)

  // For SM, BE definition of pending = "assigned to me, no rep"; assigned = "has a rep"
  const { data: pending = [] } = useSalesLeadsPending(dealershipTeamId)
  const { data: assigned = [] } = useSalesLeadsAssigned(dealershipTeamId)
  const { data: teamMembers = [] } = useTeamMembers(dealershipTeamId)

  const salesReps = useMemo<TeamMemberSummary[]>(
    () => teamMembers.filter(m => m.role === Roles.SalesRepresentative),
    [teamMembers],
  )

  const matchesQuery = (lead: ApiSalesLead) => {
    if (debouncedSearch.trim() === "") return true
    const q = debouncedSearch.toLowerCase()
    return (
      salesLeadCustomerName(lead).toLowerCase().includes(q) ||
      salesLeadVehicleName(lead).toLowerCase().includes(q) ||
      (salesLeadVin(lead) ?? "").toLowerCase().includes(q) ||
      (lead.email ?? "").toLowerCase().includes(q) ||
      (lead.phoneNumber ?? "").toLowerCase().includes(q)
    )
  }
  const filteredPending = pending.filter(matchesQuery)
  const filteredAssigned = assigned.filter(matchesQuery)

  const tabs = [
    { id: "pending", label: "Needs Rep Assignment", count: pending.length },
    { id: "assigned", label: "Assigned to Reps", count: assigned.length },
  ]
  const activeList = selectedTab === "pending" ? filteredPending : filteredAssigned

  return (
    <AppLayout>
      <div className="p-4 md:p-8 lg:p-10 max-w-[1600px] mx-auto space-y-6 md:space-y-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900">
            Sales Pipeline Management
          </h1>
          <p className="text-zinc-400">
            Assign total loss leads to your sales reps
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 md:gap-6 md:grid-cols-3">
          <SmStatCard label="Needs Rep Assignment" value={pending.length} sub="Awaiting a sales rep" icon={Clock} iconColor="text-amber-500" />
          <SmStatCard label="Assigned to Reps" value={assigned.length} sub="In progress with reps" icon={TrendingUp} iconColor="text-blue-500" />
          <SmStatCard label="Total Leads" value={pending.length + assigned.length} sub="In your queue" icon={CheckCircle} iconColor="text-zinc-700" />
        </div>

        <div className="rounded-2xl border border-zinc-200/60 bg-white p-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-4 text-zinc-400" />
            <input
              placeholder="Search by customer, vehicle, VIN, email, phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 rounded-2xl border border-zinc-200 bg-zinc-50 pl-11 pr-4 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150"
            />
          </div>
        </div>

        <div className="inline-flex p-1 rounded-2xl bg-zinc-100">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTab(tab.id)}
              className={`h-8 px-4 rounded-xl text-sm font-medium transition-all duration-150 flex items-center gap-2 ${
                selectedTab === tab.id ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500 hover:text-zinc-700"
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-md text-xs font-medium bg-amber-100 text-amber-700">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="rounded-2xl border border-zinc-200/60 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-zinc-100">
            <h3 className="text-xl font-semibold text-zinc-900">
              {selectedTab === "pending" ? "Pending Rep Assignment" : "Assigned to Reps"}
            </h3>
            <p className="text-sm text-zinc-400 mt-1">
              {selectedTab === "pending"
                ? "Pick a sales rep to work each lead"
                : "Track pipeline progress for each lead"}
            </p>
          </div>
          <div className="p-5">
            {activeList.length === 0 ? (
              <div className="text-center py-12 text-zinc-400">
                <CheckCircle className="size-12 mx-auto mb-4 opacity-50" />
                <p>{selectedTab === "pending" ? "All leads have a rep" : "No leads assigned to reps yet"}</p>
              </div>
            ) : (
              <SmLeadsTable
                leads={activeList}
                showRepColumn={selectedTab === "assigned"}
                onAssignClick={setAssignTarget}
              />
            )}
          </div>
        </div>
      </div>

      <AssignSalesRepDialog
        open={!!assignTarget}
        lead={assignTarget}
        salesReps={salesReps}
        dealershipTeamId={dealershipTeamId}
        onClose={() => setAssignTarget(null)}
      />
    </AppLayout>
  )
}

function SmStatCard({
  label, value, sub, icon: Icon, iconColor,
}: {
  label: string
  value: number
  sub: string
  icon: React.ComponentType<{ className?: string }>
  iconColor: string
}) {
  return (
    <div className="rounded-2xl border border-zinc-200/60 bg-white p-4 md:p-5">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium text-zinc-600">{label}</span>
        <Icon className={`size-5 ${iconColor}`} />
      </div>
      <div className="text-2xl md:text-3xl font-bold text-zinc-900">{value}</div>
      <p className="text-sm text-zinc-400 mt-1">{sub}</p>
    </div>
  )
}

function SmLeadsTable({
  leads, showRepColumn, onAssignClick,
}: {
  leads: ApiSalesLead[]
  showRepColumn: boolean
  onAssignClick: (lead: ApiSalesLead) => void
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-100">
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Customer</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Vehicle</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Dept.</th>
            <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Value</th>
            {showRepColumn ? (
              <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Sales Rep</th>
            ) : (
              <th className="text-left py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Created</th>
            )}
            <th className="text-right py-3 px-4 text-xs font-medium text-zinc-400 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {leads.map((lead) => {
            const customerName = salesLeadCustomerName(lead)
            const vehicleName = salesLeadVehicleName(lead)
            const vin = salesLeadVin(lead)
            const value = salesLeadEstimateValue(lead)
            return (
              <tr key={lead.id} className="hover:bg-zinc-50/50 transition-colors">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-3">
                    <div className="size-8 rounded-xl bg-zinc-100 flex items-center justify-center">
                      <User className="size-4 text-zinc-500" />
                    </div>
                    <div>
                      <p className="font-medium text-zinc-900">{customerName}</p>
                      <p className="text-xs text-zinc-400">{lead.phoneNumber || "—"}</p>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <p className="font-medium text-zinc-900">{vehicleName}</p>
                  <p className="text-xs text-zinc-400 font-mono">{vin ?? "—"}</p>
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium ${DEPT_BADGE[lead.department]}`}>
                    {DEPT_LABEL[lead.department]}
                  </span>
                </td>
                <td className="py-3 px-4 font-medium text-zinc-900 font-mono">
                  {value != null ? `$${value.toLocaleString()}` : "—"}
                </td>
                {showRepColumn ? (
                  <td className="py-3 px-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg text-xs font-medium bg-zinc-900 text-white">
                      <User className="size-3" />
                      {salesLeadUserName(lead.salesRep)}
                    </span>
                  </td>
                ) : (
                  <td className="py-3 px-4 text-zinc-400">{new Date(lead.createdAt).toLocaleDateString()}</td>
                )}
                <td className="py-3 px-4">
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => lead.phoneNumber && window.open(`tel:${lead.phoneNumber}`)}
                      disabled={!lead.phoneNumber}
                      className="size-8 rounded-lg flex items-center justify-center hover:bg-zinc-100 text-zinc-400 hover:text-zinc-900 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Phone className="size-4" />
                    </button>
                    <button
                      onClick={() => lead.email && window.open(`mailto:${lead.email}`)}
                      disabled={!lead.email}
                      className="size-8 rounded-lg flex items-center justify-center hover:bg-zinc-100 text-zinc-400 hover:text-zinc-900 transition-colors duration-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Mail className="size-4" />
                    </button>
                    {!lead.salesRep && (
                      <button
                        onClick={() => onAssignClick(lead)}
                        className="h-8 px-3 rounded-xl bg-zinc-900 text-white text-sm font-medium hover:bg-black transition-colors duration-100 flex items-center gap-1.5"
                      >
                        <UserPlus className="size-3.5" /> Assign Rep
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function AssignSalesRepDialog({
  open, lead, salesReps, dealershipTeamId, onClose,
}: {
  open: boolean
  lead: ApiSalesLead | null
  salesReps: TeamMemberSummary[]
  dealershipTeamId: number | null
  onClose: () => void
}) {
  const [selectedId, setSelectedId] = useState("")
  const assign = useAssignSalesRep(dealershipTeamId)

  const handleConfirm = () => {
    if (!lead || !selectedId) return
    assign.mutate(
      { leadId: lead.id, data: { salesRepId: Number(selectedId) } },
      {
        onSuccess: (updated) => {
          toast.success(`Assigned to ${salesLeadUserName(updated.salesRep)}`, {
            description: `${salesLeadCustomerName(updated)} added to the rep's queue.`,
          })
          setSelectedId("")
          onClose()
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : "Failed to assign rep")
        },
      },
    )
  }

  const handleClose = () => {
    setSelectedId("")
    onClose()
  }

  return (
    <AnimatePresence>
      {open && lead && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: "spring", duration: 0.3, bounce: 0.15 }}
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-100">
              <h2 className="text-lg font-semibold text-zinc-900">Assign to Sales Rep</h2>
              <button
                onClick={handleClose}
                className="size-8 rounded-lg flex items-center justify-center hover:bg-zinc-100 text-zinc-400 hover:text-zinc-900 transition-colors duration-100"
              >
                <XClose className="size-4" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <p className="text-sm text-zinc-500">
                Assign <strong className="text-zinc-900">{salesLeadCustomerName(lead)}</strong> ({salesLeadVehicleName(lead)}) to a sales rep to work the lead.
              </p>

              <div className="space-y-1.5">
                <label className="text-sm font-medium text-zinc-700">Sales Rep</label>
                {salesReps.length === 0 ? (
                  <p className="text-sm text-zinc-400 italic">
                    No Sales Reps on this dealership yet. Invite one from Settings → Team.
                  </p>
                ) : (
                  <select
                    value={selectedId}
                    onChange={(e) => setSelectedId(e.target.value)}
                    className="w-full h-10 rounded-2xl border border-zinc-200 bg-zinc-50 px-4 text-sm text-zinc-900 outline-none focus:border-zinc-400 focus:bg-white transition-all duration-150 cursor-pointer"
                  >
                    <option value="">Select Sales Rep</option>
                    {salesReps.map((rep) => (
                      <option key={rep.id} value={String(rep.id)}>
                        {`${rep.firstName} ${rep.lastName}`.trim() || rep.email} — {rep.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  onClick={handleClose}
                  className="h-10 px-4 rounded-2xl border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors duration-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={!selectedId || assign.isPending}
                  className="h-10 px-5 rounded-2xl bg-zinc-900 text-white text-sm font-medium hover:bg-black transition-colors duration-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {assign.isPending ? "Assigning…" : "Assign to Rep"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
